import { useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  FileBadge2,
  FolderOpen,
  HelpCircle,
  Home,
  LogOut,
  MessageCircle,
  Plus,
  ShieldCheck,
  UserRound,
  X,
  Menu,
} from "lucide-react";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { cn } from "@/lib/cn";
import { initialsOf } from "@/lib/format";
import { composeDisplayName } from "@/lib/person-name";
import { LanguageSwitcher } from "./LanguageSwitcher";
import Logo from "./Logo";

type NavItem = {
  to: string;
  labelKey: "nav.dashboard" | "nav.certificates" | "nav.request" | "nav.documents" | "nav.profile" | "nav.help";
  icon: ReactNode;
};

const navItems: NavItem[] = [
  { to: "/", labelKey: "nav.dashboard", icon: <Home className="h-5 w-5" /> },
  { to: "/certificados", labelKey: "nav.certificates", icon: <FileBadge2 className="h-5 w-5" /> },
  { to: "/solicitar", labelKey: "nav.request", icon: <Plus className="h-5 w-5" /> },
  { to: "/documentos", labelKey: "nav.documents", icon: <FolderOpen className="h-5 w-5" /> },
  { to: "/perfil", labelKey: "nav.profile", icon: <UserRound className="h-5 w-5" /> },
  { to: "/ayuda", labelKey: "nav.help", icon: <HelpCircle className="h-5 w-5" /> },
];

const mobileNav = navItems.filter((n) => n.labelKey !== "nav.help");

export function AppShell() {
  const { t } = useT();
  const auth = useAuth();
  const { data } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const phone = auth.status === "authenticated" ? auth.user.phone : "";
  const role = auth.status === "authenticated" ? auth.user.role : "user";
  const fullName = composeDisplayName(data.profile) || (data.profile.companyName ?? "");
  const displayName = fullName || `+${phone}`;
  const initials = fullName ? initialsOf(fullName) : (phone.slice(-2) || "??");

  return (
    <div className="relative min-h-screen bg-slate-50 lg:flex">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 flex-none border-r border-slate-200 bg-white lg:flex lg:flex-col">
        <div className="flex h-16 items-center px-5">
          <button type="button" className="flex items-center gap-2" onClick={() => navigate("/")}>
            <Logo />
          </button>
        </div>

        <nav className="flex-1 space-y-1 px-3 py-3" aria-label="Principal">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition",
                  isActive
                    ? "bg-brand-50 text-brand-700"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                )
              }
            >
              {item.icon}
              <span>{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="m-3 rounded-2xl border border-slate-200 bg-gradient-to-br from-brand-600 to-brand-700 p-4 text-white">
          <MessageCircle className="h-5 w-5" />
          <p className="mt-3 text-sm font-semibold">{t("chat.need_help")}</p>
          <p className="mt-1 text-xs text-brand-100">{t("chat.hint")}</p>
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-white underline underline-offset-4"
          >
            {t("help.contact_title")}
          </button>
        </div>

        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-3 rounded-xl p-2">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-semibold text-white">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-900">{displayName}</p>
              <p className="flex items-center gap-1.5 truncate text-xs text-slate-500">
                {role === "admin" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                    <ShieldCheck className="h-3 w-3" />
                    {t("auth.role_admin")}
                  </span>
                )}
                +{phone}
              </p>
            </div>
            <button
              type="button"
              className="btn-ghost h-8 w-8 p-0"
              aria-label={t("common.logout")}
              title={t("common.logout")}
              onClick={() => auth.signOut()}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-80 max-w-[85vw] flex-col bg-white shadow-xl animate-fade-in">
            <div className="flex h-16 items-center justify-between px-5">
              <Logo />
              <button
                type="button"
                className="btn-ghost h-9 w-9 p-0"
                onClick={() => setMobileOpen(false)}
                aria-label={t("common.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="flex-1 space-y-1 px-3 py-3">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition",
                      isActive ? "bg-brand-50 text-brand-700" : "text-slate-700 hover:bg-slate-100"
                    )
                  }
                >
                  {item.icon}
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </nav>
            <div className="space-y-3 border-t border-slate-200 p-4">
              <LanguageSwitcher />
              <button
                type="button"
                onClick={() => auth.signOut()}
                className="btn-secondary w-full"
              >
                <LogOut className="h-4 w-4" />
                {t("common.logout")}
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="min-w-0 flex-1 flex flex-col">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur">
          <div className="flex h-16 items-center justify-between gap-3 px-4 lg:px-8">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="btn-ghost h-10 w-10 p-0 lg:hidden"
                aria-label="Abrir menú"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="lg:hidden">
                <Logo compact />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <LanguageSwitcher compact />
              <button
                type="button"
                onClick={() => navigate("/solicitar")}
                className="btn-primary hidden md:inline-flex"
              >
                <Plus className="h-4 w-4" />
                {t("nav.request")}
              </button>
              <div className="lg:hidden">
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-xs font-semibold text-white">
                  {initials}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 pb-28 pt-6 sm:px-6 lg:px-8 lg:pb-10">
          <div className="mx-auto max-w-6xl animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>

      <nav
        aria-label="Navegación inferior"
        className="fixed bottom-0 left-0 right-0 z-30 border-t border-slate-200 bg-white/90 backdrop-blur safe-bottom lg:hidden"
      >
        <div className="grid grid-cols-5">
          {mobileNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition",
                  isActive ? "text-brand-700" : "text-slate-500"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      "inline-flex h-7 w-10 items-center justify-center rounded-full transition",
                      isActive && "bg-brand-50"
                    )}
                  >
                    {item.icon}
                  </span>
                  <span className="truncate">{t(item.labelKey)}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {location.pathname !== "/solicitar" && (
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          className="fixed bottom-6 right-6 z-20 hidden items-center gap-2 rounded-full bg-slate-900 pl-3 pr-5 py-3 text-sm font-medium text-white shadow-pop transition hover:bg-slate-800 lg:inline-flex"
        >
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10">
            <MessageCircle className="h-3.5 w-3.5" />
          </span>
          {t("chat.need_help")}
        </button>
      )}

      {helpOpen && <HelpSheet onClose={() => setHelpOpen(false)} />}
    </div>
  );
}

function HelpSheet({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <aside className="absolute bottom-0 right-0 w-full rounded-t-3xl bg-white p-6 shadow-xl animate-scale-in sm:right-6 sm:bottom-6 sm:w-96 sm:rounded-3xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-700">
              PahCertificar · {t("nav.help")}
            </p>
            <h3 className="mt-1 text-lg font-semibold">{t("help.contact_title")}</h3>
            <p className="mt-1 text-sm text-slate-600">{t("help.contact_body")}</p>
          </div>
          <button type="button" className="btn-ghost h-8 w-8 p-0" onClick={onClose} aria-label={t("common.close")}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-6 grid gap-3">
          <a className="btn-primary" href="mailto:hola@pahcertificar.com">
            {t("help.contact_email")}
          </a>
          <a className="btn-secondary" href="https://wa.me/573024638964" target="_blank" rel="noreferrer">
            {t("help.contact_whatsapp")}
          </a>
        </div>
      </aside>
    </div>
  );
}
